import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './CaptureInstructions.css';
import {
  ArrowForwardRounded,
  CropFreeOutlined,
  InfoOutlined,
  VisibilityOutlined,
  WbSunnyOutlined
} from '@mui/icons-material';
import {
  BackButton,
  EnterpriseButton,
  EnterpriseCard,
  EnterpriseGrid,
  EnterpriseHero,
  EnterpriseShell,
  EnterpriseTopBar,
  WarningBanner
} from './ui/EnterpriseLayout';

function CaptureInstructions() {
  const navigate = useNavigate();
  const [showWarning, setShowWarning] = useState(true);
  const [timeRemaining, setTimeRemaining] = useState(10);

  useEffect(() => {
    if (!showWarning) return;
    
    const interval = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [showWarning]);

  return (
    <EnterpriseShell>
      <EnterpriseTopBar
        right={
          <>
            <BackButton onClick={() => navigate(-1)} />
            <span className="vt-chip vt-chip--subtle">ID verification</span>
          </>
        }
      />

      <EnterpriseHero
        kicker="Document capture"
        meta="Step 1 of 2"
        title="Capture instructions"
        subtitle="Follow these guidelines to ensure your document photo is accepted on the first try."
      />

      <EnterpriseGrid>
        <EnterpriseCard>
          {showWarning && (
            <div style={{ marginBottom: 14 }}>
              <WarningBanner onClose={() => setShowWarning(false)}>
                For security reasons this session will expire in {timeRemaining} minutes.
              </WarningBanner>
            </div>
          )}

          <ul className="vt-list">
            <li className="vt-listItem">
              <VisibilityOutlined className="vt-listIcon" />
              <span>Ensure details are sharp and not covered.</span>
            </li>
            <li className="vt-listItem">
              <WbSunnyOutlined className="vt-listIcon" />
              <span>Find a well-lit area.</span>
            </li>
            <li className="vt-listItem">
              <CropFreeOutlined className="vt-listIcon" />
              <span>Keep the full document inside the frame.</span>
            </li>
          </ul>

          <div style={{ height: 12 }} />

          <div className="vt-card" style={{ padding: 14, boxShadow: 'var(--vt-shadow-soft)' }}>
            <div className="vt-listItem">
              <InfoOutlined className="vt-listIcon" />
              <span>If you have a visual or motor impairment, you may need assistance with this step.</span>
            </div>
          </div>

          <p className="vt-note">
            We recommend taking a photo, but you can also <button className="upload-link">upload a file</button> from your
            device.
          </p>
        </EnterpriseCard>

        <EnterpriseCard>
          <div className="document-illustration">
            <div className="illustration-frame">
              <div className="illustration-document">
                <div className="document-photo-area">
                  <div className="document-photo-silhouette"></div>
                </div>
                <div className="document-text-lines">
                  <div className="text-line"></div>
                  <div className="text-line"></div>
                  <div className="text-line short"></div>
                </div>
                <div className="document-mrz">
                  <span>&lt;&lt;&lt;</span>
                </div>
              </div>
            </div>
          </div>

          <div className="vt-actions">
            <EnterpriseButton variant="primary" onClick={() => navigate('/capture-document')}>
              <span>Take photo</span>
              <ArrowForwardRounded className="vt-btnIcon" />
            </EnterpriseButton>
            <EnterpriseButton variant="secondary" type="button" onClick={() => navigate('/select-id-type')}>
              Back
            </EnterpriseButton>
          </div>
        </EnterpriseCard>
      </EnterpriseGrid>
    </EnterpriseShell>
  );
}

export default CaptureInstructions;
